import { useState, useMemo } from "react";
import { Calculator as CalcIcon, TrendingUp, TrendingDown, RotateCcw } from "lucide-react";

/* ── formatting ─────────────────────────────────────────────── */
const money0 = (n) => {
  if (!isFinite(n)) return "—";
  const s = n < 0 ? "-" : "";
  return `${s}$${Math.round(Math.abs(n)).toLocaleString("en-AU")}`;
};
const money2 = (n) => {
  if (!isFinite(n)) return "—";
  const s = n < 0 ? "-" : "";
  return `${s}$${Math.abs(n).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const pct1 = (n) => `${n.toFixed(1)} %`;
const pct2 = (n) => `${n.toFixed(2)}%`;
const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));

/* ── stamp duty (estimates; standard residential, no concessions) ─ */
const prog = (v, brackets) => {
  for (const b of brackets) if (v <= b.upTo) return b.base + (v - b.over) * b.rate;
  const last = brackets[brackets.length - 1];
  return last.base + (v - last.over) * last.rate;
};
const NSW = [
  { upTo: 17000, base: 0, over: 0, rate: 0.0125 },
  { upTo: 37000, base: 212.5, over: 17000, rate: 0.015 },
  { upTo: 99000, base: 512.5, over: 37000, rate: 0.0175 },
  { upTo: 372000, base: 1597.5, over: 99000, rate: 0.035 },
  { upTo: 1240000, base: 11152.5, over: 372000, rate: 0.045 },
  { upTo: 4130000, base: 50212.5, over: 1240000, rate: 0.055 },
  { upTo: Infinity, base: 209162.5, over: 4130000, rate: 0.07 }
];
const QLD = [
  { upTo: 5000, base: 0, over: 0, rate: 0 },
  { upTo: 75000, base: 0, over: 5000, rate: 0.015 },
  { upTo: 540000, base: 1050, over: 75000, rate: 0.035 },
  { upTo: 1000000, base: 17325, over: 540000, rate: 0.045 },
  { upTo: Infinity, base: 38025, over: 1000000, rate: 0.0575 }
];
const SA = [
  { upTo: 12000, base: 0, over: 0, rate: 0.01 },
  { upTo: 30000, base: 120, over: 12000, rate: 0.02 },
  { upTo: 50000, base: 480, over: 30000, rate: 0.03 },
  { upTo: 100000, base: 1080, over: 50000, rate: 0.035 },
  { upTo: 200000, base: 2830, over: 100000, rate: 0.04 },
  { upTo: 250000, base: 6830, over: 200000, rate: 0.0425 },
  { upTo: 300000, base: 8955, over: 250000, rate: 0.0475 },
  { upTo: 500000, base: 11330, over: 300000, rate: 0.05 },
  { upTo: Infinity, base: 21330, over: 500000, rate: 0.055 }
];
const WA = [
  { upTo: 120000, base: 0, over: 0, rate: 0.019 },
  { upTo: 150000, base: 2280, over: 120000, rate: 0.0285 },
  { upTo: 360000, base: 3135, over: 150000, rate: 0.038 },
  { upTo: 725000, base: 11115, over: 360000, rate: 0.0475 },
  { upTo: Infinity, base: 28453, over: 725000, rate: 0.0515 }
];
const TAS = [
  { upTo: 3000, base: 50, over: 0, rate: 0 },
  { upTo: 25000, base: 50, over: 3000, rate: 0.0175 },
  { upTo: 75000, base: 435, over: 25000, rate: 0.0225 },
  { upTo: 200000, base: 1560, over: 75000, rate: 0.035 },
  { upTo: 375000, base: 5935, over: 200000, rate: 0.04 },
  { upTo: 725000, base: 12935, over: 375000, rate: 0.0425 },
  { upTo: Infinity, base: 27810, over: 725000, rate: 0.045 }
];
const ACT = [
  { upTo: 200000, base: 0, over: 0, rate: 0.0126 },
  { upTo: 300000, base: 2520, over: 200000, rate: 0.022 },
  { upTo: 500000, base: 4720, over: 300000, rate: 0.034 },
  { upTo: 750000, base: 11520, over: 500000, rate: 0.0432 },
  { upTo: 1000000, base: 22320, over: 750000, rate: 0.059 },
  { upTo: 1455000, base: 37070, over: 1000000, rate: 0.064 },
  { upTo: Infinity, base: 0, over: 0, rate: 0.0454 } // flat of total
];
const stampDuty = (state, v) => {
  v = Math.max(0, v);
  switch (state) {
    case "NSW": return prog(v, NSW);
    case "VIC":
      if (v <= 25000) return v * 0.014;
      if (v <= 130000) return 350 + (v - 25000) * 0.024;
      if (v <= 960000) return 2870 + (v - 130000) * 0.06;
      if (v <= 2000000) return v * 0.055;
      return 110000 + (v - 2000000) * 0.065;
    case "QLD": return prog(v, QLD);
    case "SA": return prog(v, SA);
    case "WA": return prog(v, WA);
    case "TAS": return prog(v, TAS);
    case "ACT": return v > 1455000 ? v * 0.0454 : prog(v, ACT);
    case "NT": {
      if (v <= 525000) { const V = v / 1000; return 0.06571441 * V * V + 15 * V; }
      if (v <= 3000000) return v * 0.0495;
      return v * 0.0595;
    }
    default: return 0;
  }
};
const STATES = ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "ACT", "NT"];

/* ── small row components ───────────────────────────────────── */
function InputRow({ label, value, onChange, prefix, suffix, derived, step = 1, min = 0, type = "number", placeholder, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <label className="text-sm text-neutral-300 leading-tight">{label}</label>
      <div className="flex items-center gap-3">
        <div className="flex items-center bg-[#0A100D] border border-neutral-700 rounded px-2.5 py-1.5 focus-within:border-[#36C281] transition-colors">
          {prefix && <span className="text-xs text-neutral-500 mr-1">{prefix}</span>}
          <input
            data-testid={testid}
            type={type}
            inputMode="decimal"
            min={min}
            step={step}
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
            className={`bg-transparent text-right text-[#EFE9D4] text-sm focus:outline-none ${type === "number" ? "w-20" : "w-36"}`}
          />
          {suffix && <span className="text-xs text-neutral-500 ml-1">{suffix}</span>}
        </div>
        <span className="text-sm text-[#36C281] w-24 text-right tabular-nums">{derived ?? ""}</span>
      </div>
    </div>
  );
}
function DerivedRow({ label, value, strong, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5" data-testid={testid}>
      <span className={`text-sm ${strong ? "text-[#EFE9D4] font-medium" : "text-neutral-400"}`}>{label}</span>
      <span className={`text-sm tabular-nums ${strong ? "text-[#EFE9D4] font-semibold" : "text-neutral-300"}`}>{value}</span>
    </div>
  );
}
const GroupTitle = ({ children }) => (
  <p className="text-[10px] tracking-[0.25em] uppercase text-[#36C281]/80 mt-7 mb-1 pt-5 border-t border-neutral-800 first:mt-0 first:pt-0 first:border-0">
    {children}
  </p>
);

/* ── main component ─────────────────────────────────────────── */
export default function Calculator() {
  const [suburb, setSuburb] = useState("");
  const [state, setState] = useState("NSW");
  const [price, setPrice] = useState(500000);
  const [growthPct, setGrowthPct] = useState(5);
  const [vacancyPct, setVacancyPct] = useState(3);
  const [lvrPct, setLvrPct] = useState(80);
  const [solicitor, setSolicitor] = useState(3500);
  const [stampOverride, setStampOverride] = useState(null);
  const [rentWk, setRentWk] = useState(500);
  const [council, setCouncil] = useState(200);
  const [strata, setStrata] = useState(0);
  const [water, setWater] = useState(100);
  const [insurance, setInsurance] = useState(100);
  const [maintenance, setMaintenance] = useState(100);
  const [mgmtPct, setMgmtPct] = useState(8);
  const [interestPct, setInterestPct] = useState(6.5);

  const r = useMemo(() => {
    const p = num(price);
    const lvr = num(lvrPct);
    const loan = p * (lvr / 100);
    const deposit = p - loan;
    const autoStamp = stampDuty(state, p);
    const stamp = stampOverride !== null && stampOverride !== "" ? num(stampOverride) : autoStamp;
    const purchaseCost = deposit + stamp + num(solicitor);

    const growthDollar = p * (num(growthPct) / 100);
    const annualRevenue = num(rentWk) * 52 * (1 - num(vacancyPct) / 100);
    const grossYield = p > 0 ? (annualRevenue / p) * 100 : 0;

    const councilA = num(council) * 12;
    const strataA = num(strata) * 12;
    const waterA = num(water) * 12;
    const insuranceA = num(insurance) * 12;
    const maintenanceA = num(maintenance) * 12;
    const mgmtCost = annualRevenue * (num(mgmtPct) / 100);
    const interestCost = loan * (num(interestPct) / 100);
    const monthlyRepayment = interestCost / 12;

    const recurringTotal = councilA + strataA + waterA + insuranceA + maintenanceA + mgmtCost + interestCost;
    const annualCashflow = annualRevenue - recurringTotal;
    const cashOnCash = purchaseCost > 0 ? (annualCashflow / purchaseCost) * 100 : 0;

    const g = num(growthPct) / 100;
    const fv = (yrs) => p * Math.pow(1 + g, yrs);

    return {
      loan, deposit, autoStamp, stamp, purchaseCost, growthDollar, annualRevenue, grossYield,
      councilA, strataA, waterA, insuranceA, maintenanceA, mgmtCost, interestCost, monthlyRepayment,
      annualCashflow, monthlyCashflow: annualCashflow / 12, weeklyCashflow: annualCashflow / 52, cashOnCash,
      v5: fv(5), v10: fv(10), v20: fv(20), v30: fv(30)
    };
  }, [price, growthPct, vacancyPct, lvrPct, solicitor, stampOverride, state, rentWk, council, strata, water, insurance, maintenance, mgmtPct, interestPct]);

  const positive = r.annualCashflow >= 0;
  const overridden = stampOverride !== null && stampOverride !== "";

  return (
    <section id="calculator" data-testid="calculator-section" className="bg-[#0E1612] border-t border-neutral-800">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-14">
          <div className="md:col-span-7">
            <p className="overline mb-4 flex items-center gap-2"><CalcIcon size={12} /> Investment Property Cashflow Calculator</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight">
              Know the numbers <span className="italic text-[#36C281]">before you buy</span>.
            </h2>
          </div>
          <p className="md:col-span-5 text-neutral-400 leading-relaxed self-end">
            Model purchase costs, rental yield, holding costs and projected growth. Stamp duty auto-calculates by state. Every figure updates live as you type.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* INPUTS */}
          <div className="lg:col-span-7 bg-[#131C18] border border-neutral-800 rounded-xl p-6 md:p-8">
            <GroupTitle>Property</GroupTitle>
            <InputRow label="Suburb / Address" type="text" value={suburb} onChange={setSuburb} placeholder="e.g. Earlville, Cairns" testid="calc-suburb" />
            <div className="flex items-center justify-between gap-3 py-2.5">
              <label className="text-sm text-neutral-300">State</label>
              <select
                data-testid="calc-state"
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="bg-[#0A100D] border border-neutral-700 rounded px-3 py-1.5 text-[#EFE9D4] text-sm focus:border-[#36C281] outline-none"
              >
                {STATES.map((s) => <option key={s} value={s} className="bg-[#0A100D]">{s}</option>)}
              </select>
            </div>
            <InputRow label="Property Price" prefix="$" step={10000} value={price} onChange={setPrice} testid="calc-price" />
            <InputRow label="Annual Capital Growth (10 yr avg)" suffix="%" step={0.5} value={growthPct} onChange={setGrowthPct} derived={money0(r.growthDollar)} testid="calc-growth" />
            <InputRow label="Vacancy Rate" suffix="%" step={0.5} value={vacancyPct} onChange={setVacancyPct} testid="calc-vacancy" />

            <GroupTitle>Purchase Costs</GroupTitle>
            <InputRow label="LVR (Loan to Value Ratio)" suffix="%" step={5} value={lvrPct} onChange={setLvrPct} derived={money0(r.loan)} testid="calc-lvr" />
            <DerivedRow label={`Deposit (${(100 - num(lvrPct)).toFixed(0)}%)`} value={money0(r.deposit)} testid="calc-deposit" />
            <div className="flex items-center justify-between gap-3 py-2.5">
              <label className="text-sm text-neutral-300 flex items-center gap-2">
                Stamp Duty
                {overridden && (
                  <button type="button" onClick={() => setStampOverride(null)} className="text-[#36C281] inline-flex items-center gap-1 text-[11px]" data-testid="calc-stamp-reset">
                    <RotateCcw size={11} /> auto
                  </button>
                )}
              </label>
              <div className="flex items-center gap-3">
                <div className="flex items-center bg-[#0A100D] border border-neutral-700 rounded px-2.5 py-1.5 focus-within:border-[#36C281] transition-colors">
                  <span className="text-xs text-neutral-500 mr-1">$</span>
                  <input
                    data-testid="calc-stamp"
                    type="number"
                    inputMode="decimal"
                    min={0}
                    value={overridden ? stampOverride : Math.round(r.autoStamp)}
                    onChange={(e) => setStampOverride(e.target.value === "" ? "" : Number(e.target.value))}
                    className="bg-transparent text-right text-[#EFE9D4] text-sm focus:outline-none w-24"
                  />
                </div>
                <span className="text-[11px] text-neutral-500 w-24 text-right">{overridden ? "manual" : `${state} est.`}</span>
              </div>
            </div>
            <InputRow label="Solicitor & etc." prefix="$" step={100} value={solicitor} onChange={setSolicitor} testid="calc-solicitor" />
            <div className="border-t border-neutral-800 mt-2 pt-1">
              <DerivedRow label="Purchase Cost (cash needed)" value={money0(r.purchaseCost)} strong testid="calc-purchase-cost" />
            </div>

            <GroupTitle>Rental Revenue (p.a.)</GroupTitle>
            <InputRow label="Rent (per week)" prefix="$" suffix="/wk" step={10} value={rentWk} onChange={setRentWk} derived={money0(r.annualRevenue)} testid="calc-rent" />
            <DerivedRow label="Gross Rental Yield" value={pct1(r.grossYield)} testid="calc-gross-yield" />

            <GroupTitle>Recurring Cost (p.a.)</GroupTitle>
            <InputRow label="Council" prefix="$" suffix="/mo" step={10} value={council} onChange={setCouncil} derived={money0(r.councilA)} testid="calc-council" />
            <InputRow label="Strata" prefix="$" suffix="/mo" step={10} value={strata} onChange={setStrata} derived={money0(r.strataA)} testid="calc-strata" />
            <InputRow label="Water" prefix="$" suffix="/mo" step={10} value={water} onChange={setWater} derived={money0(r.waterA)} testid="calc-water" />
            <InputRow label="Insurance (Building)" prefix="$" suffix="/mo" step={10} value={insurance} onChange={setInsurance} derived={money0(r.insuranceA)} testid="calc-insurance" />
            <InputRow label="Property Maintenance" prefix="$" suffix="/mo" step={10} value={maintenance} onChange={setMaintenance} derived={money0(r.maintenanceA)} testid="calc-maintenance" />
            <InputRow label="Prop. Mgt Commission" suffix="%" step={0.5} value={mgmtPct} onChange={setMgmtPct} derived={money0(r.mgmtCost)} testid="calc-mgmt" />
            <InputRow label="Interest from Loan" suffix="%" step={0.1} value={interestPct} onChange={setInterestPct} derived={money0(r.interestCost)} testid="calc-interest" />
            <div className="border-t border-neutral-800 mt-2 pt-1">
              <DerivedRow label="Monthly Loan Repayment (interest only)" value={money2(r.monthlyRepayment)} strong testid="calc-monthly-repayment" />
            </div>
          </div>

          {/* RESULTS */}
          <div className="lg:col-span-5 space-y-6 lg:sticky lg:top-24 self-start">
            <div
              data-testid="calc-result-cashflow"
              className={`border rounded-xl p-8 ${positive ? "border-[#36C281]/40 bg-[#36C281]/5" : "border-orange-400/40 bg-orange-400/5"}`}
            >
              <div className="overline flex items-center gap-2">
                {positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />} Cashflow before tax
              </div>
              <div className={`font-serif-display text-5xl md:text-6xl mt-3 ${positive ? "text-[#36C281]" : "text-orange-300"}`} data-testid="calc-cashflow-annual">
                {money0(r.annualCashflow)}
              </div>
              <div className="text-xs text-neutral-500 mt-1">per annum · {positive ? "positively geared" : "negatively geared"}</div>
              <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-neutral-800">
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">Per Month</div>
                  <div className={`text-2xl font-serif-display mt-1 ${positive ? "text-[#36C281]" : "text-orange-300"}`} data-testid="calc-cashflow-monthly">{money0(r.monthlyCashflow)}</div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">Per Week</div>
                  <div className={`text-2xl font-serif-display mt-1 ${positive ? "text-[#36C281]" : "text-orange-300"}`} data-testid="calc-cashflow-weekly">{money0(r.weeklyCashflow)}</div>
                </div>
              </div>
              <div className="mt-5 flex items-center justify-between">
                <span className="text-sm text-neutral-400">Cash on Cash Return p.a.</span>
                <span className={`text-lg font-medium ${r.cashOnCash >= 0 ? "text-[#36C281]" : "text-orange-300"}`} data-testid="calc-coc">{pct2(r.cashOnCash)}</span>
              </div>
            </div>

            <div className="bg-[#131C18] border border-neutral-800 rounded-xl p-8">
              <p className="overline mb-5">Projected Value · {num(growthPct)}% avg growth</p>
              <ul className="divide-y divide-neutral-800 text-sm">
                {[["in 5 Years", r.v5, "calc-v5"], ["in 10 Years", r.v10, "calc-v10"], ["in 20 Years", r.v20, "calc-v20"], ["in 30 Years", r.v30, "calc-v30"]].map(([l, v, t]) => (
                  <li key={t} className="py-3 flex items-center justify-between">
                    <span className="text-neutral-400">Property Value {l}</span>
                    <span className="text-[#EFE9D4] font-medium tabular-nums" data-testid={t}>{money0(v)}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-neutral-500 mt-5 leading-relaxed">
                Estimates only — interest-only loan, vacancy-adjusted rent, compound growth. Stamp duty is an approximation and excludes concessions. Excludes depreciation and tax effects. Not financial advice; obtain your own professional advice.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
